import {
    hasSelectedType,
    hasSelectedAssets,
    hasSelectedShifts,
    hasSelectedLossType,
    hasSelectedPlannedType
} from './../filters.js';
import { orderRows } from './../utils.js';

export const UNJUSTIFIED_MICRO_CAUSE_ID = -1;
export const UNJUSTIFIED_MAJOR_AVAILABILITY_CAUSE_ID = -2;
export const UNJUSTIFIED_MAJOR_PERFORMANCE_CAUSE_ID = -3;

/**
 * Get the id to use for a cause.
 * @param {Object} record
 * @return {Number}
 */
export function getCauseId(record) {
    if (record.cause_id === null) {
        return record.minor
            ? UNJUSTIFIED_MICRO_CAUSE_ID
            : record.type_of_loss === 'availability'
              ? UNJUSTIFIED_MAJOR_AVAILABILITY_CAUSE_ID
              : UNJUSTIFIED_MAJOR_PERFORMANCE_CAUSE_ID;
    } else {
        return parseInt(record.cause_id);
    }
}

/**
 * Get the id to use for a category.
 * @param {Object} record
 * @return {Number}
 */
export function getCategoryId(record) {
    if (record.category_id === null) {
        return record.minor
            ? UNJUSTIFIED_MICRO_CAUSE_ID
            : record.type_of_loss === 'availability'
              ? UNJUSTIFIED_MAJOR_AVAILABILITY_CAUSE_ID
              : UNJUSTIFIED_MAJOR_PERFORMANCE_CAUSE_ID;
    } else {
        return parseInt(record.category_id);
    }
}

/**
 * Get the name to use for a cause.
 * @param {Object} record
 * @return {String}
 */
export function getCauseName(record) {
    if (record.cause_id === null) {
        if (getCauseId(record) === -1) {
            return 'Other (minor)';
        } else if (getCauseId(record) === -2) {
            return 'Other (major) - Availability';
        } else if (getCauseId(record) === -3) {
            return 'Other (major) - Performance';
        }
    } else {
        return record.cause_name;
    }
}

/**
 * Get the name to use for a category.
 * @param {Object} record
 * @return {String}
 */
export function getCategoryName(record) {
    if (record.category_name === null) {
        return null;
    } else {
        return record.category_name;
    }
}

/**
 * Get cause from the report if it exists, otherwise use default values.
 * @param {Object} causes
 * @param {Object} record
 * @return {Object}
 */
export function getCauseLoss(report, record, secondarySort) {
    const causeId = getCauseId(record);
    if (!(causeId in report.causes)) {
        if (secondarySort === 'assets') {
            // initialize subtotals for the new cause
            report.causes[causeId] = {
                id: causeId,
                name: getCauseName(record),
                typeOfLoss: record.type_of_loss,
                planned: record.planned,
                nbEvents: 0,
                totalDuration: 0,
                assets: {}
            };
        } else {
            // initialize subtotals for the new cause
            report.causes[causeId] = {
                id: causeId,
                name: getCauseName(record),
                typeOfLoss: record.type_of_loss,
                planned: record.planned,
                nbEvents: 0,
                totalDuration: 0,
                products: {}
            };
        }
    }
    return report.causes[causeId];
}

/**
 * Get category from the report if it exists, otherwise use default values.
 * @param {Object} causes
 * @param {Object} record
 * @param {String} secondarySort
 * @return {Object}
 */
export function getCategoryLoss(report, record, secondarySort) {
    const categoryId = getCategoryId(record);
    if (!(categoryId in report.categories)) {
        if (secondarySort === 'assets') {
            // initialize subtotals for the new category
            report.categories[categoryId] = {
                id: categoryId,
                name: record.category_name
                    ? record.category_name
                    : categoryId === -1
                      ? 'Uncategorized (Micro)'
                      : record.type_of_loss === 'availability'
                        ? 'Uncategorized (Major) - Availability'
                        : 'Uncategorized (Major) - Performance',
                typeOfLoss: record.type_of_loss,
                planned: record.planned,
                nbEvents: 0,
                totalDuration: 0,
                assets: {}
            };
        } else {
            // initialize subtotals for the new category
            report.categories[categoryId] = {
                id: categoryId,
                name: record.category_name
                    ? record.category_name
                    : categoryId === -1
                      ? 'Uncategorized (Micro)'
                      : record.type_of_loss === 'availability'
                        ? 'Uncategorized (Major) - Availability'
                        : 'Uncategorized (Major) - Performance',
                typeOfLoss: record.type_of_loss,
                planned: record.planned,
                nbEvents: 0,
                totalDuration: 0,
                products: {}
            };
        }
    }
    return report.categories[categoryId];
}

/**
 * Get asset/product from a cause row if it exists, otherwise use default values.
 * @param {Object} cause
 * @param {Object} record
 * @param {String} secondarySort
 * @return {Object}
 */
export function getPrimaryGroupingLoss(cause, record, secondarySort) {
    if (secondarySort === 'assets') {
        if (!(record.asset_id in cause.assets)) {
            cause['assets'][record.asset_id] = {
                assetId: record.asset_id,
                causeId: cause.id,
                totalDuration: 0,
                nbEvents: 0,
                phases: new Set(),
                products: {}
            };
        }
        return cause['assets'][record.asset_id];
    } else {
        if (!(record.product_id in cause.products)) {
            cause['products'][record.product_id] = {
                productId: record.product_id,
                productName: record.product_name,
                causeId: cause.id,
                totalDuration: 0,
                nbEvents: 0,
                phases: new Set(),
                assets: {}
            };
        }
        return cause['products'][record.product_id];
    }
}

/**
 * Get product/asset from primary grouping if it exists, otherwise use default values.
 * @param {Object} primaryGroupingRecord
 * @param {Object} record
 * @param {String} secondarySort
 * @returns {Object}
 */
export function getSecondaryGroupingLoss(
    primaryGroupingRecord,
    record,
    secondarySort
) {
    if (secondarySort === 'assets') {
        if (!(record.product_id in primaryGroupingRecord.products)) {
            primaryGroupingRecord['products'][record.product_id] = {
                causeId: record.cause_id,
                productId: record.product_id,
                productName: record.product_name,
                totalDuration: 0,
                nbEvents: 0
            };
        }
        return primaryGroupingRecord['products'][record.product_id];
    } else {
        if (!(record.asset_id in primaryGroupingRecord.assets)) {
            primaryGroupingRecord['assets'][record.asset_id] = {
                assetId: record.asset_id,
                causeId: record.causeId,
                totalDuration: 0,
                nbEvents: 0
            };
        }
        return primaryGroupingRecord['assets'][record.asset_id];
    }
}

/**
 * Get asset/product from a category row if it exists, otherwise use default values.
 * @param {Object} category
 * @param {Object} record
 * @param {String} secondarySort
 * @returns {Object}
 */
export function getPrimaryGroupingLossCategory(
    category,
    record,
    secondarySort
) {
    if (secondarySort === 'assets') {
        if (!(record.asset_id in category.assets)) {
            category['assets'][record.asset_id] = {
                assetId: record.asset_id,
                categoryId: category.id,
                totalDuration: 0,
                nbEvents: 0,
                phases: new Set(),
                products: {}
            };
        }
        return category['assets'][record.asset_id];
    } else {
        if (!(record.product_id in category.products)) {
            category['products'][record.product_id] = {
                productId: record.product_id,
                productName: record.product_name,
                causeId: category.id,
                totalDuration: 0,
                nbEvents: 0,
                phases: new Set(),
                assets: {}
            };
        }
        return category['products'][record.product_id];
    }
}

/**
 * Get product/asset from primary grouping if it exists, otherwise use default values.
 * @param {Object} primaryGroupingRecord
 * @param {Object} record
 * @param {String} secondarySort
 * @returns
 */
export function getSecondaryGroupingLossCategory(
    primaryGroupingRecord,
    record,
    secondarySort
) {
    if (secondarySort === 'assets') {
        if (!(record.product_id in primaryGroupingRecord.products)) {
            primaryGroupingRecord['products'][record.product_id] = {
                assetId: record.asset_id,
                productId: record.product_id,
                productName: record.product_name,
                totalDuration: 0,
                nbEvents: 0
            };
        }
        return primaryGroupingRecord['products'][record.product_id];
    } else {
        if (!(record.asset_id in primaryGroupingRecord.assets)) {
            primaryGroupingRecord['assets'][record.asset_id] = {
                assetId: record.asset_id,
                causeId: record.causeId,
                totalDuration: 0,
                nbEvents: 0
            };
        }
        return primaryGroupingRecord['assets'][record.asset_id];
    }
}

/**
 * Determine whether to include a record in the report.
 * @param {Object} record
 * @param {Object} filters
 * @return {Boolean}
 *
 * The filter should be of the following form:
 * { minor: (bool), major: (bool), assetIds: (set), shiftIds: (set) }
 */
export function isIncluded(record, filters) {
    return (
        hasSelectedType(record, filters) &&
        hasSelectedLossType(record, filters) &&
        hasSelectedPlannedType(record, filters) &&
        hasSelectedAssets(record, filters) &&
        hasSelectedShifts(record, filters)
    );
}

/**
 * Sets and calculates the cumulative downtimes in the table.
 * @param {Object[]} rows
 * @param {Number} total
 */
export function calcCumulativeDowntimes(rows, total) {
    let cumulativeTotal = 0;
    for (const row of rows) {
        cumulativeTotal += row.totalDuration;
        if (total > 0) {
            row['cumulativeDowntime'] = cumulativeTotal / total;
        } else {
            row['cumulativeDowntime'] = 1;
        }
    }
}

/**
 * Build the loss report.
 * @param {Object} record
 * @param {Object} filters
 * @param {String} secondarySort
 * @return {Object}
 *
 * The filter should be of the following form:
 * { minor: (bool), major: (bool), assetIds: (set), shiftIds: (set) }
 */
export function buildLossReport(records, filters, secondarySort) {
    const report = {
        causes: {},
        categories: {},
        totals: { totalDuration: 0, nbEvents: 0 }
    };

    if (records !== null) {
        for (const record of records) {
            if (isIncluded(record, filters)) {
                // get objects
                // for causes
                const causeLoss = getCauseLoss(report, record, secondarySort);
                const primaryGroupingLoss = getPrimaryGroupingLoss(
                    causeLoss,
                    record,
                    secondarySort
                );
                const secondaryGroupingLoss = getSecondaryGroupingLoss(
                    primaryGroupingLoss,
                    record,
                    secondarySort
                );

                // for categories
                const categoryLoss = getCategoryLoss(
                    report,
                    record,
                    secondarySort
                );
                const primaryGroupingLossCategory =
                    getPrimaryGroupingLossCategory(
                        categoryLoss,
                        record,
                        secondarySort
                    );
                const secondaryGroupingLossCategory =
                    getSecondaryGroupingLossCategory(
                        primaryGroupingLossCategory,
                        record,
                        secondarySort
                    );

                // Currently, there is a bug where some phases have a end date that is
                // before the start date. This causes the downtime duration to be negative.
                // A workaround is to set the downtime duration to 0 if it is negative.
                const downtime_duration = Math.max(record.downtime_duration, 0);
                causeLoss.totalDuration += downtime_duration;
                primaryGroupingLoss.totalDuration += downtime_duration;
                secondaryGroupingLoss.totalDuration += downtime_duration;

                categoryLoss.totalDuration += downtime_duration;
                primaryGroupingLossCategory.totalDuration += downtime_duration;
                secondaryGroupingLossCategory.totalDuration +=
                    downtime_duration;

                report.totals.totalDuration += downtime_duration;

                // count events if phase has not been seen
                if (!primaryGroupingLoss.phases.has(record.phase_uuid)) {
                    causeLoss.nbEvents += 1;
                    primaryGroupingLoss.nbEvents += 1;
                    secondaryGroupingLoss.nbEvents += 1;

                    categoryLoss.nbEvents += 1;
                    primaryGroupingLossCategory.nbEvents += 1;
                    secondaryGroupingLossCategory.nbEvents += 1;

                    report.totals.nbEvents += 1;

                    primaryGroupingLoss.phases.add(record.phase_uuid);
                    primaryGroupingLossCategory.phases.add(record.phase_uuid);
                }
            }
        }
    }
    return report;
}

/**
 * Populate the rows in the loss report table
 * @param {Object} object
 * @param {String} groupBy
 * @param {String} secondarySort
 * @param {Object} table
 * @param {String} orderBy
 * @param {String} descending
 */
function populateRows(
    object,
    groupBy,
    secondarySort,
    table,
    orderBy,
    descending
) {
    // const categories = report.categories;
    if (secondarySort === 'assets') {
        for (const [o_id, data] of Object.entries(object)) {
            const row = {
                id: parseInt(o_id),
                name: data.name,
                totalDuration: data.totalDuration,
                nbEvents: data.nbEvents,
                cumulativeDowntime: 0,
                assets: []
            };

            // build asset rows

            let assetsTotalDuration = 0;
            for (const [assetId, assetData] of Object.entries(data.assets)) {
                assetsTotalDuration += assetData.totalDuration;
                const assetRow = {
                    assetId: parseInt(assetId),
                    id: parseInt(data.id),
                    name: data.name,
                    typeOfLoss: data.typeOfLoss,
                    planned: data.planned,
                    totalDuration: assetData.totalDuration,
                    cumulativeDowntime: 0,
                    nbEvents: assetData.nbEvents,
                    products: []
                };
                let productsTotalDuration = 0;
                for (const [productId, productData] of Object.entries(
                    assetData.products
                )) {
                    productsTotalDuration += productData.totalDuration;
                    const productRow = {
                        assetId: parseInt(assetId),
                        productId: parseInt(productId),
                        productName: productData.productName,
                        id: parseInt(data.id),
                        name: data.name,
                        typeOfLoss: data.typeOfLoss,
                        planned: data.planned,
                        totalDuration: productData.totalDuration,
                        cumulativeDowntime: 0,
                        nbEvents: productData.nbEvents
                    };
                    assetRow.products.push(productRow);
                }
                orderRows(assetRow.products, orderBy, descending);
                calcCumulativeDowntimes(
                    assetRow.products,
                    productsTotalDuration
                );
                row.assets.push(assetRow);
            }
            orderRows(row.assets, orderBy, descending);
            calcCumulativeDowntimes(row.assets, assetsTotalDuration);
            table.rows.push(row);
        }
    } else {
        for (const [o_id, data] of Object.entries(object)) {
            const row = {
                id: parseInt(o_id),
                name: data.name,
                totalDuration: data.totalDuration,
                nbEvents: data.nbEvents,
                cumulativeDowntime: 0,
                products: []
            };

            // build product rows
            let productsTotalDuration = 0;
            for (const [productId, productData] of Object.entries(
                data.products
            )) {
                productsTotalDuration += productData.totalDuration;
                const productRow = {
                    productId: parseInt(productId),
                    productName: productData.productName,
                    id: data.id,
                    name: data.name,
                    typeOfLoss: data.typeOfLoss,
                    planned: data.planned,
                    totalDuration: productData.totalDuration,
                    cumulativeDowntime: 0,
                    nbEvents: productData.nbEvents,
                    assets: []
                };
                let assetsTotalDuration = 0;
                for (const [assetId, assetData] of Object.entries(
                    productData.assets
                )) {
                    assetsTotalDuration += assetData.totalDuration;
                    const assetRow = {
                        assetId: parseInt(assetId),
                        id: (Math.random() + 1).toString(36).substring(7),
                        name: data.name,
                        typeOfLoss: data.typeOfLoss,
                        planned: data.planned,
                        totalDuration: productData.totalDuration,
                        cumulativeDowntime: 0,
                        nbEvents: productData.nbEvents
                    };
                    productRow.assets.push(assetRow);
                }
                orderRows(productRow.assets, orderBy, descending);
                calcCumulativeDowntimes(productRow.assets, assetsTotalDuration);
                row.products.push(productRow);
            }
            orderRows(row.products, orderBy, descending);
            calcCumulativeDowntimes(row.products, productsTotalDuration);
            table.rows.push(row);
        }
    }
}

/**
 * Builds a table given a loss report
 * @param {Object} report
 * @param {String} orderBy
 * @param {Boolean} descending
 * @return {Object}
 */
export function buildTable(
    report,
    groupBy,
    secondarySort,
    orderBy,
    descending = true
) {
    const table = {
        rows: [],
        totals: report.totals
    };
    let selectedOption;

    if (groupBy === 'categories') {
        selectedOption = report.categories;
    } else if (groupBy === 'causes') {
        selectedOption = report.causes;
    }

    populateRows(
        selectedOption,
        groupBy,
        secondarySort,
        table,
        orderBy,
        descending
    );
    orderRows(table.rows, orderBy, descending);
    calcCumulativeDowntimes(table.rows, report.totals.totalDuration);
    return table;
}
